import React, { useState } from 'react'
import {
  Box,
  Button,
  Card,
  TextField,
  MenuItem,
  Typography,
  Avatar,
  IconButton,
  Chip,
  Tooltip,
  Grid,
  InputAdornment,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  CircularProgress,
  TablePagination,
  Checkbox,
  CardContent,
  CardActions,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
  Collapse,
  Badge,
  Menu,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  CheckCircle,
  Cancel,
  Warning,
  ShoppingCart,
  Refresh,
  Clear,
  ViewList,
  ViewModule,
  ContentCopy as CopyIcon,
  Tune as TuneIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { usePermissions } from '../hooks/usePermissions'
import {
  useGetProductsQuery,
  useDeleteProductMutation,
  useUpdateProductMutation,
} from '../store/services/productApi'
import { useGetCategoriesQuery } from '../store/services/categoryApi'
import { Product, ProductStatus } from '../types/product'
import { DataTable, TableColumn, TableAction, StatusChip } from '../components/ui'
import BulkActionBar, { getProductBulkActions } from '../components/ui/BulkActionBar'
import ProgressManager from '../components/ui/ProgressManager'
import useBulkOperations from '../hooks/useBulkOperations'
import ProductImportDialog from '../components/products/ProductImportDialog'
import ProductExportDialog from '../components/products/ProductExportDialog'
import AgenticProductWizard from '../components/products/AgenticProductWizard'
import { getAbsoluteImageUrl } from '../utils/imageUtils'

export const isProductLiveOnPortal = (product: Product): { 
  isLive: boolean
  label: string
  reason: string
  color: 'success' | 'error' | 'warning' | 'default'
} => {
  if (!product.is_active) {
    return { isLive: false, label: 'Not Live (Inactive)', reason: 'Product is deactivated in system (is_active = false)', color: 'error' }
  }
  if (product.is_online_enabled === false) {
    return { isLive: false, label: 'Storefront Off', reason: 'Online store visibility is disabled (is_online_enabled = false)', color: 'error' }
  }
  if (product.status === ProductStatus.DRAFT) {
    return { isLive: false, label: 'Not Live (Draft)', reason: 'Product is in Draft status (not published to portal)', color: 'warning' }
  }
  if (product.status === ProductStatus.ARCHIVED) {
    return { isLive: false, label: 'Not Live (Archived)', reason: 'Product is Archived and hidden from customer portal', color: 'default' }
  }
  if (product.status === ProductStatus.DISCONTINUED) {
    return { isLive: false, label: 'Not Live (Discontinued)', reason: 'Product is Discontinued', color: 'default' }
  }
  if (!product.category_id) {
    return { isLive: false, label: 'No Category', reason: 'Missing Category assignment (required for customer portal navigation)', color: 'warning' }
  }
  const hasPrice = Number(product.sale_price) > 0 || (product.variants && product.variants.some(v => Number(v.sale_price) > 0))
  if (!hasPrice) {
    return { isLive: false, label: 'No Price Set', reason: 'Product sale price is ₹0 and has no priced variants', color: 'warning' }
  }
  return { isLive: true, label: 'Live on Portal', reason: 'Product is active, published, and visible to buyers on customer portal', color: 'success' }
}

interface PresetTab {
  id: string
  label: string
  status: ProductStatus | ''
  is_deal?: boolean
  is_clearance?: boolean
  is_set_selling?: boolean
}

const PRESET_TABS: PresetTab[] = [
  { id: 'ALL', label: 'All', status: '' },
  { id: 'ACTIVE', label: 'Active', status: ProductStatus.ACTIVE },
  { id: 'NOT_LIVE', label: '🚫 Not Live on Portal', status: '' },
  { id: 'SET_SELLING', label: '📦 Sold in Sets', status: '', is_set_selling: true },
  { id: 'DRAFT', label: 'Draft', status: ProductStatus.DRAFT },
  { id: 'OUT_OF_STOCK', label: 'Out of Stock', status: ProductStatus.OUT_OF_STOCK },
  { id: 'DEALS', label: '🔥 Hot Deals', status: '', is_deal: true },
  { id: 'CLEARANCE', label: '🏷️ Clearance', status: '', is_clearance: true },
  { id: 'ARCHIVED', label: 'Archived', status: ProductStatus.ARCHIVED },
  { id: 'DISCONTINUED', label: 'Discontinued', status: ProductStatus.DISCONTINUED },
]

const Products: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [page, setPage] = useState(() => Number(searchParams.get('page')) || 1)
  const [pageSize, setPageSize] = useState(() => Number(searchParams.get('pageSize')) || 10)
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '')
  const [debouncedSearch, setDebouncedSearch] = useState(() => searchParams.get('search') || '')
  const [statusFilter, setStatusFilter] = useState<ProductStatus | ''>(() => (searchParams.get('status') as ProductStatus) || '')
  const [isDealFilter, setIsDealFilter] = useState(() => searchParams.get('is_deal') === 'true')
  const [isClearanceFilter, setIsClearanceFilter] = useState(() => searchParams.get('is_clearance') === 'true')
  const [isSetSellingFilter, setIsSetSellingFilter] = useState(() => searchParams.get('is_set_selling') === 'true')
  const [activeTabId, setActiveTabId] = useState(() => {
    if (searchParams.get('is_set_selling') === 'true') return 'SET_SELLING'
    if (searchParams.get('is_deal') === 'true') return 'DEALS'
    if (searchParams.get('is_clearance') === 'true') return 'CLEARANCE'
    return searchParams.get('status') || 'ALL'
  })

  const [categoryFilter, setCategoryFilter] = useState(() => searchParams.get('category') || '')
  const [priceMin, setPriceMin] = useState(() => searchParams.get('priceMin') || '')
  const [priceMax, setPriceMax] = useState(() => searchParams.get('priceMax') || '')
  const [stockMin, setStockMin] = useState(() => searchParams.get('stockMin') || '')
  const [stockMax, setStockMax] = useState(() => searchParams.get('stockMax') || '')
  const [debouncedPriceMin, setDebouncedPriceMin] = useState(() => searchParams.get('priceMin') || '')
  const [debouncedPriceMax, setDebouncedPriceMax] = useState(() => searchParams.get('priceMax') || '')
  const [debouncedStockMin, setDebouncedStockMin] = useState(() => searchParams.get('stockMin') || '')
  const [debouncedStockMax, setDebouncedStockMax] = useState(() => searchParams.get('stockMax') || '')
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [expressWizardOpen, setExpressWizardOpen] = useState(false)
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table')
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)

  // Status Quick Menu State
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<null | HTMLElement>(null)
  const [activeProductForStatus, setActiveProductForStatus] = useState<Product | null>(null)

  // Bulk operations
  const {
    operations,
    executeBulkOperation,
    clearCompletedOperations,
    clearAllOperations,
  } = useBulkOperations()
  const { enqueueSnackbar } = useSnackbar()
  const { hasPermission } = usePermissions()
  const theme = useTheme()
  const isBelowMd = useMediaQuery(theme.breakpoints.down('md'))
  const isBelowLg = useMediaQuery(theme.breakpoints.down('lg'))

  const isFirstSearchDebounce = React.useRef(true)
  const isFirstRangeDebounce = React.useRef(true)
  const isFirstFilterReset = React.useRef(true)

  // Debounce search term
  React.useEffect(() => {
    if (isFirstSearchDebounce.current) {
      isFirstSearchDebounce.current = false
      return
    }
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
      setPage(1)
    }, 500)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Debounce price/stock range filters
  React.useEffect(() => {
    if (isFirstRangeDebounce.current) {
      isFirstRangeDebounce.current = false
      return
    }
    const timer = setTimeout(() => {
      setDebouncedPriceMin(priceMin)
      setDebouncedPriceMax(priceMax)
      setDebouncedStockMin(stockMin)
      setDebouncedStockMax(stockMax)
      setPage(1)
    }, 500)
    return () => clearTimeout(timer)
  }, [priceMin, priceMax, stockMin, stockMax])

  // Reset page when filters change
  React.useEffect(() => {
    if (isFirstFilterReset.current) {
      isFirstFilterReset.current = false
      return
    }
    setPage(1)
  }, [statusFilter, categoryFilter, isDealFilter, isClearanceFilter, isSetSellingFilter])

  // Keep URL in sync with filters
  React.useEffect(() => {
    const params: Record<string, string> = {}
    if (debouncedSearch) params.search = debouncedSearch
    if (statusFilter) params.status = statusFilter
    if (isDealFilter) params.is_deal = 'true'
    if (isClearanceFilter) params.is_clearance = 'true'
    if (isSetSellingFilter) params.is_set_selling = 'true'
    if (categoryFilter) params.category = categoryFilter
    if (debouncedPriceMin) params.priceMin = debouncedPriceMin
    if (debouncedPriceMax) params.priceMax = debouncedPriceMax
    if (debouncedStockMin) params.stockMin = debouncedStockMin
    if (debouncedStockMax) params.stockMax = debouncedStockMax
    if (page > 1) params.page = String(page)
    if (pageSize !== 10) params.pageSize = String(pageSize)
    setSearchParams(params, { replace: true })
  }, [
    debouncedSearch, statusFilter, isDealFilter, isClearanceFilter, isSetSellingFilter, categoryFilter,
    debouncedPriceMin, debouncedPriceMax, debouncedStockMin, debouncedStockMax,
    page, pageSize, setSearchParams,
  ])

  // Fetch products from API
  const { data, isLoading, isFetching, refetch, error } = useGetProductsQuery({
    page,
    per_page: pageSize,
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
    is_deal: isDealFilter || undefined,
    is_clearance: isClearanceFilter || undefined,
    is_set_selling: isSetSellingFilter || undefined,
    category_id: categoryFilter || undefined,
    price_min: debouncedPriceMin ? Number(debouncedPriceMin) : undefined,
    price_max: debouncedPriceMax ? Number(debouncedPriceMax) : undefined,
    stock_min: debouncedStockMin ? Number(debouncedStockMin) : undefined,
    stock_max: debouncedStockMax ? Number(debouncedStockMax) : undefined,
  })

  // Fetch categories for filter dropdown
  const { data: categoriesData, isLoading: isLoadingCategories } = useGetCategoriesQuery({ per_page: 100 })

  // Error handling
  React.useEffect(() => {
    if (error) {
      const err = error as any
      let errorMessage = 'Failed to load products.'
      if (err?.status === 401) errorMessage = 'Please log in to view products.'
      else if (err?.status === 404) errorMessage = 'Products endpoint not found.'
      else if (err?.status >= 500) errorMessage = 'Server error. Please try again later.'
      enqueueSnackbar(errorMessage, { variant: 'error' })
    }
  }, [error, enqueueSnackbar])

  const filteredProducts = React.useMemo(() => {
    let items = data?.items || []
    if (activeTabId === 'NOT_LIVE') {
      items = items.filter(p => !isProductLiveOnPortal(p).isLive)
    }
    return items
  }, [data, activeTabId])

  const [deleteProduct] = useDeleteProductMutation()
  const [updateProduct] = useUpdateProductMutation()

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this product?')) return
    try {
      await deleteProduct(id).unwrap()
      enqueueSnackbar('Product deleted successfully', { variant: 'success' })
    } catch (err: any) {
      enqueueSnackbar('Failed to delete product', { variant: 'error' })
    }
  }

  // Quick inline status change for operations team
  const handleQuickStatusChange = async (product: Product, newStatus: ProductStatus) => {
    try {
      await updateProduct({ id: product.id, data: { status: newStatus } }).unwrap()
      enqueueSnackbar(`Status updated to ${newStatus}`, { variant: 'success' })
    } catch (err: any) {
      enqueueSnackbar(err?.data?.detail || 'Failed to update status', { variant: 'error' })
    } finally {
      setStatusMenuAnchor(null)
      setActiveProductForStatus(null)
    }
  }

  // One-click publish to portal
  const handleQuickMakeLive = async (product: Product) => {
    try {
      await updateProduct({ 
        id: product.id, 
        data: { 
          status: ProductStatus.ACTIVE, 
          is_active: true, 
          is_online_enabled: true 
        } 
      }).unwrap()
      enqueueSnackbar(`"${product.name}" is now Live on Customer Portal`, { variant: 'success' })
    } catch (err: any) {
      enqueueSnackbar(err?.data?.detail || 'Failed to publish product to portal', { variant: 'error' })
    } finally {
      setStatusMenuAnchor(null)
      setActiveProductForStatus(null)
    }
  }

  const handleCopySku = (sku: string) => {
    navigator.clipboard.writeText(sku)
    enqueueSnackbar(`Copied SKU "${sku}" to clipboard`, { variant: 'info' })
  }

  const handleTabChange = (newTabId: string) => {
    setActiveTabId(newTabId)
    const tab = PRESET_TABS.find(t => t.id === newTabId)
    if (tab) {
      setStatusFilter(tab.status)
      setIsDealFilter(Boolean(tab.is_deal))
      setIsClearanceFilter(Boolean(tab.is_clearance))
      setIsSetSellingFilter(Boolean(tab.is_set_selling))
      setPage(1)
    }
  }

  const returnTo = `/admin/products${searchParams.toString() ? `?${searchParams.toString()}` : ''}`

  const handleView = (product: Product) => {
    navigate(`/admin/products/${product.id}/view`, { state: { returnTo } })
  }

  const handleEdit = (product: Product) => {
    navigate(`/admin/products/${product.id}/edit`, { state: { returnTo } })
  }

  const handleCreate = () => {
    navigate('/admin/products/create')
  }

  // Bulk Actions
  const handleBulkDelete = async (selectedIds: string[]) => {
    await executeBulkOperation(
      selectedIds,
      {
        title: 'Delete Products',
        description: `Deleting ${selectedIds.length} products`,
        totalItems: selectedIds.length,
      },
      async (items, onProgress) => {
        let successful = 0
        let failed = 0
        const errors: string[] = []
        for (let i = 0; i < items.length; i++) {
          try {
            await deleteProduct(items[i]).unwrap()
            successful++
            onProgress(i + 1, `Deleted product ${i + 1} of ${items.length}`)
          } catch (error: any) {
            failed++
            errors.push(error?.data?.detail || error?.message || `Failed to delete product ${items[i]}`)
            onProgress(i + 1, `Failed to delete product ${i + 1} of ${items.length}`)
          }
        }
        return { successful, failed, warnings: 0, errors }
      }
    )
    setSelectedRows([])
  }

  const csvEscape = (value: unknown): string => {
    const str = value === null || value === undefined ? '' : String(value)
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str
  }

  const handleBulkExport = async (selectedIds: string[]) => {
    await executeBulkOperation(
      selectedIds,
      {
        title: 'Export Products',
        description: `Exporting ${selectedIds.length} products to CSV`,
        totalItems: selectedIds.length,
      },
      async (items, onProgress) => {
        const selectedProducts = filteredProducts.filter(p => items.includes(p.id))
        const header = [
          'Name', 'SKU', 'Item Code', 'HSN Code', 'Category', 'Brand', 'Status',
          'MRP', 'Sale Price', 'Wholesale Price', 'Purchase Price', 'GST Rate (%)',
          'MOQ', 'Inner Box Qty', 'Carton Qty', 'Set Size', 'Is Set Selling', 'Weight (kg)', 'Is Active',
        ]
        const lines = [header.join(',')]

        for (let i = 0; i < items.length; i++) {
          const product = selectedProducts.find(p => p.id === items[i])
          if (product) {
            lines.push([
              csvEscape(product.name),
              csvEscape(product.sku),
              csvEscape(product.item_code),
              csvEscape(product.hsn_code),
              csvEscape((product as any).category?.name || ''),
              csvEscape((product as any).brand?.name || ''),
              csvEscape(product.status),
              csvEscape(product.mrp),
              csvEscape(product.sale_price),
              csvEscape(product.wholesale_price),
              csvEscape(product.purchase_price),
              csvEscape(product.gst_rate),
              csvEscape(product.moq),
              csvEscape(product.inner_box_quantity),
              csvEscape(product.carton_quantity),
              csvEscape(product.set_size || 1),
              csvEscape(product.is_set_selling ? 'Yes' : 'No'),
              csvEscape(product.weight),
              csvEscape(product.is_active ? 'Yes' : 'No'),
            ].join(','))
          }
          onProgress(i + 1, `Processed product ${i + 1} of ${items.length}`)
        }

        const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `selected_products_${new Date().toISOString().slice(0, 10)}.csv`
        a.click()
        URL.revokeObjectURL(url)

        return { successful: selectedProducts.length, failed: items.length - selectedProducts.length, warnings: 0, errors: [] }
      }
    )
    setSelectedRows([])
  }

  const bulkActions = getProductBulkActions(
    handleBulkDelete,
    handleBulkExport
  ).filter((action) => {
    if (action.id === 'delete') return hasPermission('products.delete')
    if (action.id === 'export') return hasPermission('products.bulk_export')
    return true
  })

  const PRODUCT_STATUS_COLORS = {
    active: { color: 'success' as const, icon: <CheckCircle sx={{ fontSize: 13 }} /> },
    archived: { color: 'error' as const, icon: <Cancel sx={{ fontSize: 13 }} /> },
    draft: { color: 'warning' as const, icon: <EditIcon sx={{ fontSize: 13 }} /> },
    discontinued: { color: 'default' as const, icon: <Cancel sx={{ fontSize: 13 }} /> },
    out_of_stock: { color: 'warning' as const, icon: <Warning sx={{ fontSize: 13 }} /> },
  }

  // Table Columns
  const columns: TableColumn[] = [
    {
      id: 'product',
      label: 'Product Info',
      width: 300,
      render: (_, row: Product) => {
        const imageUrl = getAbsoluteImageUrl(row.image) || (row.images && row.images.length > 0 ? getAbsoluteImageUrl(row.images[0]) : null)
        const hasImage = !!imageUrl

        return (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.5 }}>
            {hasImage ? (
              <Box
                component="img"
                src={imageUrl}
                alt={row.name}
                onError={(e: any) => {
                  e.target.style.display = 'none'
                  if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'
                }}
                sx={{
                  width: 44,
                  height: 44,
                  objectFit: 'cover',
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'grey.100',
                  flexShrink: 0,
                  mt: 0.25,
                }}
              />
            ) : null}
            <Avatar
              variant="rounded"
              sx={{
                width: 44,
                height: 44,
                bgcolor: 'primary.main',
                fontSize: '1rem',
                fontWeight: 600,
                display: hasImage ? 'none' : 'flex',
                borderRadius: 1.5,
                flexShrink: 0,
                mt: 0.25,
              }}
            >
              {row.name.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography 
                variant="body2" 
                fontWeight={600} 
                sx={{ 
                  mb: 0.5, 
                  fontSize: '0.85rem',
                  lineHeight: 1.35,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  wordBreak: 'break-word',
                }} 
                title={row.name}
              >
                {row.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Chip
                  label={row.sku}
                  size="small"
                  variant="outlined"
                  onClick={() => handleCopySku(row.sku)}
                  title="Click to copy SKU"
                  onDelete={() => handleCopySku(row.sku)}
                  deleteIcon={<CopyIcon sx={{ fontSize: '11px !important' }} />}
                  sx={{
                    fontSize: '0.68rem',
                    height: 19,
                    maxWidth: 190,
                    fontFamily: 'monospace',
                    borderColor: 'primary.light',
                    color: 'primary.main',
                    cursor: 'pointer',
                    '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                    '&:hover': { bgcolor: 'primary.50' },
                  }}
                />
                {row.hsn_code && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                    HSN: {row.hsn_code}
                  </Typography>
                )}
                {row.is_deal && (
                  <Chip label="DEAL" size="small" color="error" sx={{ fontSize: '0.65rem', height: 18, fontWeight: 700 }} />
                )}
                {row.is_clearance && (
                  <Chip label="CLEARANCE" size="small" color="warning" sx={{ fontSize: '0.65rem', height: 18, fontWeight: 700 }} />
                )}
              </Box>
            </Box>
          </Box>
        )
      },
    },
    {
      id: 'category',
      label: 'Category & Brand',
      width: 140,
      render: (_, row: Product) => (
        <Box sx={{ maxWidth: 140 }}>
          <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.82rem', lineHeight: 1.25, wordBreak: 'break-word' }}>
            {row.category?.name || 'Uncategorized'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.72rem', mt: 0.25, wordBreak: 'break-word' }}>
            {(row as any).brand?.name || (row.product_metadata?.brand as string) || 'No Brand'}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'pricing',
      label: 'B2B Pricing Tiers',
      width: 140,
      render: (_, row: Product) => {
        const mrp = Number(row.mrp) || 0
        const salePrice = Number(row.sale_price) || 0
        const wholesalePrice = Number(row.wholesale_price) || 0
        const purchasePrice = Number(row.purchase_price) || 0

        return (
          <Box sx={{ py: 0.25, minWidth: 120 }}>
            <Typography variant="body2" fontWeight={700} color="success.main" sx={{ fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
              Sale: ₹{salePrice.toLocaleString('en-IN')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.25, flexWrap: 'wrap' }}>
              {mrp > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                  MRP: ₹{mrp.toLocaleString('en-IN')}
                </Typography>
              )}
              {wholesalePrice > 0 && (
                <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.72rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  WS: ₹{wholesalePrice.toLocaleString('en-IN')}
                </Typography>
              )}
            </Box>
            {purchasePrice > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem', fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                Cost: ₹{purchasePrice.toLocaleString('en-IN')}
              </Typography>
            )}
          </Box>
        )
      },
    },
    {
      id: 'packaging',
      label: 'Packaging & Set Selling',
      width: 150,
      render: (_, row: Product) => {
        const innerPack = row.inner_box_quantity || 0
        const cartonSize = row.carton_quantity || 0
        const moq = row.moq || 1
        const isSetSelling = row.is_set_selling
        const setSize = row.set_size || 1

        return (
          <Box sx={{ minWidth: 130 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25, flexWrap: 'wrap' }}>
              {isSetSelling ? (
                <Chip
                  label={`SET OF ${setSize}`}
                  size="small"
                  color="success"
                  sx={{ fontWeight: 700, fontSize: '0.65rem', height: 18 }}
                />
              ) : (
                <Chip
                  label="SINGLE PCS"
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.65rem', height: 18 }}
                />
              )}
              <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                MOQ: {moq}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
              Box: {innerPack} pcs · Ctn: {cartonSize} pcs
            </Typography>
          </Box>
        )
      },
    },
    {
      id: 'inventory',
      label: 'Stock',
      width: 80,
      render: (_, row: Product) => {
        const totalStock = row.total_stock || 0
        const minStock = (row as any).min_stock_level || 5
        const isLow = totalStock <= minStock

        return (
          <Chip
            label={`${totalStock}`}
            size="small"
            color={totalStock === 0 ? 'error' : isLow ? 'warning' : 'default'}
            variant={totalStock === 0 ? 'filled' : 'outlined'}
            sx={{ fontWeight: 700, fontSize: '0.75rem', height: 22, minWidth: 44 }}
          />
        )
      },
    },
    {
      id: 'portal_live',
      label: 'Storefront Live',
      width: 140,
      render: (_, row: Product) => {
        const liveInfo = isProductLiveOnPortal(row)
        const totalStock = row.total_stock || 0
        const isOutOfStock = totalStock === 0

        return (
          <Tooltip title={liveInfo.reason} arrow placement="top">
            <Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 0.25, alignItems: 'flex-start' }}>
              <Chip
                icon={liveInfo.isLive ? <CheckCircle sx={{ fontSize: '13px !important' }} /> : <Cancel sx={{ fontSize: '13px !important' }} />}
                label={liveInfo.label}
                size="small"
                color={liveInfo.color}
                variant={liveInfo.isLive ? 'filled' : 'outlined'}
                sx={{
                  fontWeight: 700,
                  fontSize: '0.68rem',
                  height: 22,
                  '& .MuiChip-icon': { ml: '4px' },
                }}
              />
              {liveInfo.isLive && isOutOfStock && (
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'error.main', fontWeight: 600, lineHeight: 1 }}>
                  ⚠️ 0 Stock
                </Typography>
              )}
            </Box>
          </Tooltip>
        )
      },
    },
    {
      id: 'status',
      label: 'Status',
      width: 100,
      render: (_, row: Product) => {
        const canUpdate = hasPermission('products.update')
        return (
          <Box
            onClick={(e) => {
              if (canUpdate) {
                setStatusMenuAnchor(e.currentTarget)
                setActiveProductForStatus(row)
              }
            }}
            sx={{ cursor: canUpdate ? 'pointer' : 'default', display: 'inline-block' }}
            title={canUpdate ? 'Click to change status' : ''}
          >
            <StatusChip
              status={row.status.toLowerCase()}
              customColors={PRODUCT_STATUS_COLORS}
              sx={{ height: 22, fontSize: '0.7rem', minWidth: 80 }}
            />
          </Box>
        )
      },
    },
  ]

  const visibleColumns = columns.filter((column) => {
    if (isBelowMd) return ['product', 'pricing', 'portal_live', 'status'].includes(column.id)
    if (isBelowLg) return ['product', 'category', 'pricing', 'inventory', 'portal_live', 'status'].includes(column.id)
    return true
  })

  const actions: TableAction[] = [
    {
      icon: <VisibilityIcon fontSize="small" />,
      label: 'View',
      onClick: handleView,
      color: 'primary',
    },
    {
      icon: <ShoppingCart fontSize="small" />,
      label: 'Storefront View',
      onClick: (row: Product) => {
        const baseUrl = (import.meta as any).env.VITE_CUSTOMER_PORTAL_URL || 'http://localhost:3001'
        window.open(`${baseUrl}/products/${row.id}`, '_blank')
      },
      color: 'success',
    },
    ...(hasPermission('products.update') ? [{
      icon: <EditIcon fontSize="small" />,
      label: 'Edit',
      onClick: handleEdit,
      color: 'secondary' as const,
    }] : []),
    ...(hasPermission('products.delete') ? [{
      icon: <DeleteIcon fontSize="small" />,
      label: 'Delete',
      onClick: (row: Product) => handleDelete(row.id),
      color: 'error' as const,
    }] : []),
  ]

  const hasActiveFilters = Boolean(
    searchTerm || statusFilter || isDealFilter || isClearanceFilter || isSetSellingFilter || categoryFilter || priceMin || priceMax || stockMin || stockMax
  )

  const activeFilterCount = [
    Boolean(searchTerm),
    Boolean(statusFilter),
    Boolean(isDealFilter),
    Boolean(isClearanceFilter),
    Boolean(isSetSellingFilter),
    Boolean(categoryFilter),
    Boolean(priceMin || priceMax),
    Boolean(stockMin || stockMax),
  ].filter(Boolean).length

  const handleClearAllFilters = () => {
    setSearchTerm('')
    setStatusFilter('')
    setIsDealFilter(false)
    setIsClearanceFilter(false)
    setIsSetSellingFilter(false)
    setActiveTabId('ALL')
    setCategoryFilter('')
    setPriceMin('')
    setPriceMax('')
    setStockMin('')
    setStockMax('')
    setPage(1)
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
      {/* Sleek Compact Header Toolbar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
            <ShoppingCart fontSize="small" />
          </Avatar>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.25rem', lineHeight: 1.2 }}>
                Products
              </Typography>
              <Chip
                label={`${data?.total || 0} items`}
                size="small"
                color="primary"
                sx={{ height: 20, fontSize: '0.72rem', fontWeight: 600 }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary">
              Manage inventory, B2B pricing tiers, set selling configurations, and catalog statuses
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
          {hasPermission('products.bulk_import') && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<UploadIcon fontSize="small" />}
              onClick={() => setImportDialogOpen(true)}
              sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
            >
              Import
            </Button>
          )}
          {hasPermission('products.bulk_export') && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon fontSize="small" />}
              onClick={() => setExportDialogOpen(true)}
              sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
            >
              Export Catalog
            </Button>
          )}
          {hasPermission('products.create') && (
            <>
              <Button
                size="small"
                variant="contained"
                startIcon={<AutoAwesomeIcon fontSize="small" />}
                onClick={() => setExpressWizardOpen(true)}
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #7c4dff 0%, #651fff 100%)',
                  color: 'white',
                  '&:hover': { background: 'linear-gradient(135deg, #651fff 0%, #4527a0 100%)' }
                }}
              >
                Express AI Create ✨
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon fontSize="small" />}
                onClick={handleCreate}
                sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
              >
                Add Product
              </Button>
            </>
          )}
        </Stack>
      </Paper>

      {/* Preset Status & Deals/Clearance/Set-Selling Tabs */}
      <Paper
        elevation={0}
        sx={{
          mb: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          px: 1,
        }}
      >
        <Tabs
          value={activeTabId}
          onChange={(_, val) => handleTabChange(val)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 44,
            '& .MuiTab-root': {
              minHeight: 44,
              py: 1,
              px: 2,
              fontSize: '0.85rem',
              fontWeight: 600,
              textTransform: 'none',
            },
          }}
        >
          {PRESET_TABS.map((tab) => (
            <Tab key={tab.id} label={tab.label} value={tab.id} />
          ))}
        </Tabs>
      </Paper>

      {/* Compact Search & Control Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search name, SKU, HSN, brand..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={2.5}>
            <TextField
              select
              fullWidth
              size="small"
              label="Category"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              disabled={isLoadingCategories}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            >
              <MenuItem value="">All Categories</MenuItem>
              {categoriesData?.items.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>
                  {cat.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <Badge badgeContent={(priceMin || priceMax || stockMin || stockMax) ? '•' : 0} color="primary">
              <Button
                fullWidth
                size="small"
                variant={showAdvancedFilters ? 'contained' : 'outlined'}
                color={showAdvancedFilters ? 'primary' : 'inherit'}
                startIcon={<TuneIcon fontSize="small" />}
                endIcon={showAdvancedFilters ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                sx={{ borderRadius: 1.5, height: 38, textTransform: 'none', fontWeight: 600 }}
              >
                Ranges
              </Button>
            </Badge>
          </Grid>

          <Grid item xs={12} sm={12} md={3.5}>
            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
              {hasActiveFilters && (
                <Button
                  size="small"
                  color="error"
                  onClick={handleClearAllFilters}
                  startIcon={<Clear fontSize="small" />}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Reset ({activeFilterCount})
                </Button>
              )}

              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, newMode) => newMode && setViewMode(newMode)}
                size="small"
                aria-label="View mode"
              >
                <ToggleButton value="table" aria-label="Table View">
                  <Tooltip title="Table View">
                    <ViewList fontSize="small" />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="card" aria-label="Card View">
                  <Tooltip title="Card View">
                    <ViewModule fontSize="small" />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>

              <Tooltip title="Refresh list">
                <IconButton
                  size="small"
                  onClick={() => refetch()}
                  disabled={isFetching}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
                >
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>

        {/* Advanced Filters Collapsible Drawer */}
        <Collapse in={showAdvancedFilters}>
          <Box sx={{ pt: 2, mt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
            <Grid container spacing={1.5}>
              <Grid item xs={6} sm={3} md={2.5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Price Min (₹)"
                  type="number"
                  value={priceMin}
                  onChange={(e) => setPriceMin(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2.5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Price Max (₹)"
                  type="number"
                  value={priceMax}
                  onChange={(e) => setPriceMax(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2.5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Stock Min"
                  type="number"
                  value={stockMin}
                  onChange={(e) => setStockMin(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={2.5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Stock Max"
                  type="number"
                  value={stockMax}
                  onChange={(e) => setStockMax(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={2}>
                <Button
                  fullWidth
                  size="small"
                  variant="outlined"
                  color="inherit"
                  onClick={() => {
                    setPriceMin('')
                    setPriceMax('')
                    setStockMin('')
                    setStockMax('')
                  }}
                  sx={{ height: 38, borderRadius: 1.5, textTransform: 'none' }}
                >
                  Clear Ranges
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        {/* Active Filter Chips Bar */}
        {hasActiveFilters && (
          <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider', display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Filters:
            </Typography>
            {searchTerm && (
              <Chip
                label={`Search: "${searchTerm}"`}
                size="small"
                onDelete={() => setSearchTerm('')}
                color="primary"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.72rem' }}
              />
            )}
            {statusFilter && (
              <Chip
                label={`Status: ${statusFilter}`}
                size="small"
                onDelete={() => { setStatusFilter(''); setActiveTabId('ALL') }}
                color="primary"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.72rem' }}
              />
            )}
            {isSetSellingFilter && (
              <Chip
                label="📦 Sold in Sets"
                size="small"
                onDelete={() => { setIsSetSellingFilter(false); setActiveTabId('ALL') }}
                color="success"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600 }}
              />
            )}
            {isDealFilter && (
              <Chip
                label="🔥 Hot Deals"
                size="small"
                onDelete={() => { setIsDealFilter(false); setActiveTabId('ALL') }}
                color="error"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600 }}
              />
            )}
            {isClearanceFilter && (
              <Chip
                label="🏷️ Clearance"
                size="small"
                onDelete={() => { setIsClearanceFilter(false); setActiveTabId('ALL') }}
                color="warning"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600 }}
              />
            )}
            {categoryFilter && (
              <Chip
                label={`Category: ${categoriesData?.items.find(c => c.id === categoryFilter)?.name || 'Selected'}`}
                size="small"
                onDelete={() => setCategoryFilter('')}
                color="primary"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.72rem' }}
              />
            )}
            {(priceMin || priceMax) && (
              <Chip
                label={`Price: ₹${priceMin || '0'} - ${priceMax ? `₹${priceMax}` : '∞'}`}
                size="small"
                onDelete={() => { setPriceMin(''); setPriceMax('') }}
                color="primary"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.72rem' }}
              />
            )}
            {(stockMin || stockMax) && (
              <Chip
                label={`Stock: ${stockMin || '0'} - ${stockMax || '∞'}`}
                size="small"
                onDelete={() => { setStockMin(''); setStockMax('') }}
                color="primary"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.72rem' }}
              />
            )}
          </Box>
        )}
      </Paper>

      {/* Bulk Action Bar */}
      {selectedRows.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <BulkActionBar
            selectedItems={selectedRows}
            totalItems={data?.total || 0}
            onSelectAll={(selected) => {
              if (selected) setSelectedRows(filteredProducts.map(p => p.id))
              else setSelectedRows([])
            }}
            onClearSelection={() => setSelectedRows([])}
            actions={bulkActions}
            itemName="products"
          />
        </Box>
      )}

      {/* Loading Indicator */}
      {(isLoading || isFetching) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {/* Products Table View */}
      {!isLoading && !isFetching && viewMode === 'table' && (
        <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          <DataTable
            dense
            columns={visibleColumns}
            rows={filteredProducts}
            actions={actions}
            page={page - 1}
            rowsPerPage={pageSize}
            totalRows={data?.total || 0}
            onPageChange={(newPage) => setPage(newPage + 1)}
            onRowsPerPageChange={setPageSize}
            isLoading={isLoading || isFetching}
            emptyMessage="No products match your criteria"
            enableSelection={true}
            selectedRows={selectedRows}
            onSelectRow={(id) => {
              setSelectedRows(prev => prev.includes(id) ? prev.filter(rId => rId !== id) : [...prev, id])
            }}
            onSelectAllRows={(selected) => {
              if (selected) setSelectedRows(filteredProducts.map(p => p.id))
              else setSelectedRows([])
            }}
          />
        </Paper>
      )}

      {/* Products Cards View */}
      {!isLoading && !isFetching && viewMode === 'card' && (
        <Box>
          {filteredProducts.length === 0 ? (
            <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Typography color="text.secondary">No products match your criteria</Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {filteredProducts.map((product) => {
                const imageUrl = getAbsoluteImageUrl(product.image) || (product.images && product.images.length > 0 ? getAbsoluteImageUrl(product.images[0]) : null)
                const salePrice = Number(product.sale_price) || 0
                const wholesalePrice = Number(product.wholesale_price) || 0
                const mrp = Number(product.mrp) || 0
                const isSelected = selectedRows.includes(product.id)
                const totalStock = product.total_stock || 0

                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                    <Card
                      elevation={0}
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 2,
                        border: '1.5px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        bgcolor: 'background.paper',
                        transition: 'all 0.15s ease',
                        '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.08)', borderColor: 'primary.light' },
                      }}
                    >
                      <Box sx={{ position: 'relative' }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => {
                            setSelectedRows(prev =>
                              prev.includes(product.id) ? prev.filter(id => id !== product.id) : [...prev, product.id]
                            )
                          }}
                          sx={{ position: 'absolute', top: 4, left: 4, bgcolor: 'background.paper', borderRadius: '50%', zIndex: 1 }}
                          size="small"
                        />

                        {imageUrl ? (
                          <Box
                            component="img"
                            src={imageUrl}
                            alt={product.name}
                            sx={{ width: '100%', height: 130, objectFit: 'cover', bgcolor: 'grey.100' }}
                          />
                        ) : (
                          <Box sx={{ width: '100%', height: 130, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
                            <Avatar variant="rounded" sx={{ width: 44, height: 44, fontSize: '1.1rem', bgcolor: 'primary.main' }}>
                              {product.name.charAt(0).toUpperCase()}
                            </Avatar>
                          </Box>
                        )}
                      </Box>

                      <CardContent sx={{ flex: 1, p: 1.5, pb: '4px !important' }}>
                        <Typography variant="body2" fontWeight={600} noWrap title={product.name} sx={{ fontSize: '0.875rem' }}>
                          {product.name}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25, mb: 0.75 }}>
                          <Chip
                            label={product.sku}
                            size="small"
                            variant="outlined"
                            onClick={() => handleCopySku(product.sku)}
                            title="Click to copy SKU"
                            sx={{
                              fontSize: '0.65rem',
                              height: 18,
                              fontFamily: 'monospace',
                              borderColor: 'primary.light',
                              color: 'primary.main',
                              cursor: 'pointer',
                            }}
                          />
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.72rem' }}>
                            {product.category?.name || 'Uncategorized'}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="body2" fontWeight={700} color="success.main" sx={{ fontSize: '0.85rem' }}>
                            Sale: ₹{salePrice.toLocaleString('en-IN')}
                          </Typography>
                          {mrp > 0 && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                              MRP: ₹{mrp.toLocaleString('en-IN')}
                            </Typography>
                          )}
                          {wholesalePrice > 0 && (
                            <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.72rem', fontWeight: 600 }}>
                              WS: ₹{wholesalePrice.toLocaleString('en-IN')}
                            </Typography>
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.5, mt: 0.5 }}>
                          <StatusChip
                            status={product.status.toLowerCase()}
                            customColors={PRODUCT_STATUS_COLORS}
                            sx={{ height: 20, fontSize: '0.68rem' }}
                          />
                          <Chip
                            label={`Stock: ${totalStock}`}
                            size="small"
                            color={totalStock === 0 ? 'error' : totalStock <= 5 ? 'warning' : 'default'}
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }}
                          />
                        </Box>
                      </CardContent>

                      <CardActions sx={{ justifyContent: 'space-between', px: 1.5, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          {product.is_set_selling ? `Set of ${product.set_size || 1}` : `MOQ: ${product.moq || 1}`}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.25 }}>
                          <Tooltip title="View">
                            <IconButton size="small" color="primary" onClick={() => handleView(product)}>
                              <VisibilityIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          {hasPermission('products.update') && (
                            <Tooltip title="Edit">
                              <IconButton size="small" color="secondary" onClick={() => handleEdit(product)}>
                                <EditIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {hasPermission('products.delete') && (
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => handleDelete(product.id)}>
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </CardActions>
                    </Card>
                  </Grid>
                )
              })}
            </Grid>
          )}

          {/* Cards View Pagination */}
          <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', mt: 2 }}>
            <TablePagination
              component="div"
              count={data?.total || 0}
              page={page - 1}
              onPageChange={(_, newPage) => setPage(newPage + 1)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => setPageSize(parseInt(e.target.value, 10))}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Paper>
        </Box>
      )}

      {/* Inline Quick Status Change Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => {
          setStatusMenuAnchor(null)
          setActiveProductForStatus(null)
        }}
      >
        <MenuItem 
          onClick={() => activeProductForStatus && handleQuickMakeLive(activeProductForStatus)}
          sx={{ fontWeight: 700, color: 'success.dark', bgcolor: 'success.50', mb: 0.5, '&:hover': { bgcolor: 'success.100' } }}
        >
          <AutoAwesomeIcon fontSize="small" sx={{ color: 'success.main', mr: 1 }} /> ⚡ Make Live on Portal
        </MenuItem>
        <MenuItem onClick={() => activeProductForStatus && handleQuickStatusChange(activeProductForStatus, ProductStatus.ACTIVE)}>
          <CheckCircle fontSize="small" sx={{ color: 'success.main', mr: 1 }} /> Active
        </MenuItem>
        <MenuItem onClick={() => activeProductForStatus && handleQuickStatusChange(activeProductForStatus, ProductStatus.DRAFT)}>
          <EditIcon fontSize="small" sx={{ color: 'warning.main', mr: 1 }} /> Draft
        </MenuItem>
        <MenuItem onClick={() => activeProductForStatus && handleQuickStatusChange(activeProductForStatus, ProductStatus.OUT_OF_STOCK)}>
          <Warning fontSize="small" sx={{ color: 'warning.main', mr: 1 }} /> Out of Stock
        </MenuItem>
        <MenuItem onClick={() => activeProductForStatus && handleQuickStatusChange(activeProductForStatus, ProductStatus.ARCHIVED)}>
          <Cancel fontSize="small" sx={{ color: 'error.main', mr: 1 }} /> Archived
        </MenuItem>
        <MenuItem onClick={() => activeProductForStatus && handleQuickStatusChange(activeProductForStatus, ProductStatus.DISCONTINUED)}>
          <Cancel fontSize="small" sx={{ color: 'text.secondary', mr: 1 }} /> Discontinued
        </MenuItem>
      </Menu>

      {/* Progress Manager */}
      <ProgressManager
        operations={operations}
        onClearCompleted={clearCompletedOperations}
        onClearAll={clearAllOperations}
      />

      {/* Import/Export Dialogs */}
      <ProductImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onSuccess={() => {
          refetch()
          setImportDialogOpen(false)
        }}
      />
      <ProductExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        initialStatus={statusFilter}
        initialCategory={categoryFilter}
      />
      <AgenticProductWizard
        open={expressWizardOpen}
        onClose={() => setExpressWizardOpen(false)}
        onSuccess={() => refetch()}
      />
    </Box>
  )
}

export default Products